import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import settingsService from '../../../../services/settings.service';

const ClBrands = () => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [addingNew, setAddingNew] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const clinicId = useEyeconxStore((state) => state.user.clinicId);

    const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm();

    const fetchBrands = async () => {
        setLoading(true);
        try {
            const response = await settingsService.getCLBrands(clinicId);
            setBrands(response.data);
        } catch (error) {
            console.error('Error fetching brands:', error);
        } finally {
            setLoading(false);
        }
    };

    const addBrand = async (data) => {
        try {
            const brandData = {
                brandName: data.brandName,
                isActive: data.status,
                clinicId: clinicId,
            };
            const response = await settingsService.addUpdateCLBrands(clinicId, brandData);
            setBrands((prev) => [...prev, response.data]);
            setAddingNew(false);
            reset();
        } catch (error) {
            console.error('Error adding brand:', error);
        }
    };

    const updateBrand = async (brandId, data) => {
        try {
            const updatedBrandData = {
                id: brandId,
                brandName: data.brandName,
                isActive: data.status,
                clinicId: clinicId,
            };
            const response = await settingsService.addUpdateCLBrands(clinicId, updatedBrandData);
            setBrands((prev) =>
                prev.map((brand) =>
                    brand.id === brandId
                        ? { ...brand, ...response.data, brandName: data.brandName, isActive: data.status }
                        : brand
                )
            );
            setEditingId(null);
        } catch (error) {
            console.error('Error updating brand:', error);
        }
    };

    const deleteBrand = async (brandId) => {
        try {
            await settingsService.deleteCLBrands(clinicId, brandId);
            setBrands((prev) => prev.filter((brand) => brand.id !== brandId));
            setDeleteConfirm(null);
        } catch (error) {
            console.error('Error deleting brand:', error);
        }
    };

    const handleEdit = (brand) => {
        setEditingId(brand.id);
        setValue('brandName', brand.brandName);
        setValue('status', brand.isActive);
    };

    const handleCancel = () => {
        setEditingId(null);
        setAddingNew(false);
        reset();
    };

    const handleAddNew = () => {
        setAddingNew(true);
        setEditingId(null);
        reset({ brandName: '', status: true });
    };

    useEffect(() => {
        fetchBrands();
    }, []);

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-0">
                        <i className="bi bi-eye me-2 text-primary"></i> Contact Lens Brands
                    </h4>
                    <p className="text-muted mb-0">Manage your contact lens brand inventory</p>
                </div>
                {!addingNew && (
                    <button className="btn btn-outline-primary" onClick={handleAddNew}>
                        <i className="bi bi-plus-circle me-2"></i> Add New Brand
                    </button>
                )}
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2">Loading brands...</p>
                </div>
            ) : brands.length === 0 && !addingNew ? (
                <div className="text-center py-5">
                    <i className="bi bi-inbox display-1 text-muted"></i>
                    <h5 className="text-muted mt-3">No brands found</h5>
                    <p className="text-muted">Get started by adding your first contact lens brand</p>
                    <button className="btn btn-primary mt-2" onClick={handleAddNew}>
                        <i className="bi bi-plus-circle me-2"></i> Add First Brand
                    </button>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover align-middle table-striped">
                        <thead>
                        <tr>
                            <th scope="col">Status</th>
                            <th scope="col" className="ps-4"><i className="bi bi-tag me-2"></i> Brand Name</th>
                            <th scope="col">Created</th>
                            <th scope="col">Last Modified</th>
                            <th scope="col" className="text-end pe-4">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {/* Add New Row */}
                        {addingNew && (
                            <tr className="border-top bg-light position-relative">
                                <td>
                                    <div className="form-check form-switch">
                                        <input className="form-check-input" type="checkbox" {...register('status')} defaultChecked />
                                    </div>
                                </td>
                                <td className="ps-4">
                                    <div className="d-flex align-items-center">

                                        <div className="flex-grow-1">
                                            <input
                                                type="text"
                                                className={`form-control ${errors.brandName ? 'is-invalid' : ''}`}
                                                placeholder="Enter brand name..."
                                                {...register('brandName', {
                                                    required: 'Brand name is required',
                                                    minLength: { value: 2, message: 'Brand name must be at least 2 characters' },
                                                })}
                                            />
                                            {errors.brandName && <div className="invalid-feedback">{errors.brandName.message}</div>}
                                        </div>
                                    </div>
                                </td>
                                <td><small className="text-muted">-</small></td>
                                <td><small className="text-muted">-</small></td>
                                <td className="text-end pe-4">
                                    <div className="btn-group">
                                        <button className="btn btn-success" onClick={handleSubmit(addBrand)}>
                                            <i className="bi bi-check"></i>
                                        </button>
                                        <button className="btn btn-outline-secondary" onClick={handleCancel}>
                                            <i className="bi bi-x"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {/* Existing Brands */}
                        {brands.map((brand) => (
                            <tr key={brand.id} className="border-top position-relative">
                                {/* Delete Confirmation Overlay */}
                                {deleteConfirm?.id === brand.id && (
                                    <td colSpan="5" className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-90 d-flex justify-content-between align-items-center px-4" style={{ zIndex: 10 }}>
                                        <div className="d-flex align-items-center">
                                            <i className="bi bi-exclamation-triangle-fill text-danger me-2 fs-4"></i>
                                            <strong>Confirm delete "{brand.brandName}"?</strong>
                                        </div>
                                        <div className="btn-group">
                                            <button className="btn btn-danger text-white" onClick={() => deleteBrand(brand.id)}>
                                                <i className="bi bi-trash me-1"></i> Confirm
                                            </button>
                                            <button className="btn btn-outline-secondary" onClick={() => setDeleteConfirm(null)}>
                                                Cancel
                                            </button>
                                        </div>
                                    </td>
                                )}

                                <td>
                                    {editingId === brand.id ? (
                                        <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" {...register('status')} defaultChecked={brand.isActive} />
                                        </div>
                                    ) : (
                                        <span className={`badge ${brand.isActive ? 'bg-success' : 'bg-secondary'} ${brand.isActive ? 'text-white' : 'text-white'} fs-6`}>
                        <i className={`bi ${brand.isActive ? 'bi-check-circle' : 'bi-pause-circle'} me-1`}></i>
                                            {brand.isActive ? 'Active' : 'Inactive'}
                      </span>
                                    )}
                                </td>
                                <td className="ps-4">
                                    <div className="d-flex align-items-center">

                                        <div className="flex-grow-1">
                                            {editingId === brand.id ? (
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.brandName ? 'is-invalid' : ''}`}
                                                    defaultValue={brand.brandName}
                                                    {...register('brandName', {
                                                        required: 'Brand name is required',
                                                        minLength: { value: 2, message: 'Brand name must be at least 2 characters' },
                                                    })}
                                                />
                                            ) : (
                                                <>
                                                    <h6 className="mb-0 fw-semibold">{brand.brandName}</h6>
                                                    <small className="text-muted">ID: {brand.id.slice(0, 8)}...</small>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td><small className="text-muted">{brand.createdAt}</small></td>
                                <td><small className="text-muted">{brand.modifiedAt}</small></td>
                                <td className="text-end pe-4">
                                    <div className="btn-group">
                                        {editingId === brand.id ? (
                                            <>
                                                <button className="btn btn-success" onClick={handleSubmit((data) => updateBrand(brand.id, data))}>
                                                    <i className="bi bi-check"></i>
                                                </button>
                                                <button className="btn  btn-outline-secondary" onClick={handleCancel}>
                                                    <i className="bi bi-x"></i>
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button className="btn btn-outline-info" onClick={() => handleEdit(brand)}>
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                                <button className="btn btn-outline-danger" onClick={() => setDeleteConfirm(brand)}>
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

export default ClBrands;
